import store from '@/store';

/**
 * Build availability stats for a single normalized segment AFTER the user has
 * picked seats (checkout / booking review flows).
 *
 * A segment represents a single bus ride (one-way, return, or a leg of a
 * connection). When the segment does not support seat selection (interurban
 * tenants, flights, open tickets, etc.) the returned payload only carries
 * `has_seat_selection: false` so downstream Mixpanel consumers can filter it.
 *
 * The payload includes both the pre-selection (`available_seats_before`) and
 * post-selection (`available_seats_after`) counts so analytics can measure the
 * impact of the user's choice on the bus occupancy.
 *
 * @param {object} segment - Normalized segment data.
 * @param {boolean} segment.hasSeatSelection - Whether this segment allows seat selection.
 * @param {number} [segment.totalSeats] - Total seats in the bus for this segment.
 * @param {Array} [segment.occupiedSeats] - Seats already taken by other passengers.
 * @param {Array} [segment.selectedSeats] - Seats currently picked by the user.
 * @returns {object} Flat availability payload ready to attach to a Mixpanel event.
 */
export function getSegmentAvailabilityAfterSelection(segment) {
  if (!segment || !segment.hasSeatSelection) {
    return { has_seat_selection: false };
  }

  const { totalSeats } = segment;
  const occupiedSeats = segment.occupiedSeats || [];
  const selectedSeats = segment.selectedSeats || [];

  // The segment supports seat selection but we could not resolve the seat map
  // (e.g. the Redux slice was not populated yet). Report the capability as
  // `true` and omit the numeric fields so Mixpanel can filter these cases
  // apart from segments that genuinely don't offer seat selection.
  if (!totalSeats || totalSeats <= 0) {
    return { has_seat_selection: true };
  }

  const occupiedCount = occupiedSeats.length;
  const selectedCount = selectedSeats.length;

  return {
    has_seat_selection: true,
    total_seats: totalSeats,
    available_seats_before: totalSeats - occupiedCount,
    selected_seats_count: selectedCount,
    available_seats_after: totalSeats - occupiedCount - selectedCount,
    occupancy_percentage: (occupiedCount + selectedCount) / totalSeats,
  };
}

/**
 * Build availability stats for a single normalized segment BEFORE the user
 * picks any seat (seat map view flow). Unlike
 * `getSegmentAvailabilityAfterSelection`, selected seats are intentionally
 * excluded because the user has not made a choice yet.
 *
 * @param {object} segment - Normalized segment data.
 * @param {boolean} segment.hasSeatSelection - Whether this segment allows seat selection.
 * @param {number} [segment.totalSeats] - Total seats in the bus for this segment.
 * @param {Array} [segment.occupiedSeats] - Seats already taken by other passengers.
 * @returns {object} Flat availability payload ready to attach to a Mixpanel event.
 */
export function getSegmentAvailabilityBeforeSelection(segment) {
  if (!segment || !segment.hasSeatSelection) {
    return { has_seat_selection: false };
  }

  const { totalSeats } = segment;
  const occupiedSeats = segment.occupiedSeats || [];

  // Same rationale as `getSegmentAvailabilityAfterSelection`: capability is
  // reported even when the seat map is not resolvable yet.
  if (!totalSeats || totalSeats <= 0) {
    return { has_seat_selection: true };
  }

  const occupiedCount = occupiedSeats.length;

  return {
    has_seat_selection: true,
    total_seats: totalSeats,
    available_seats: totalSeats - occupiedCount,
    occupied_seats: occupiedCount,
    occupancy_percentage: occupiedCount / totalSeats,
  };
}

/**
 * Flatten a way (departs/returns) into its segment fragments.
 *
 * When a trip has connections the purchase stores an aggregate "general" trip
 * as `fragments[0]` and the actual buses on `fragments.slice(1)`. Each bus in
 * a connection has its own seat map, total seats, and occupancy, so we skip
 * the aggregate and emit one entry per real bus to capture the per-leg
 * availability. Without connections the single `fragments[0]` is the segment.
 *
 * @param {object} way - Purchase departs/returns object.
 * @returns {Array<object>} Segment fragments to iterate over, one per bus.
 */
function getWaySegmentFragments(way) {
  if (!way || !Array.isArray(way.fragments) || way.fragments.length === 0) {
    return [];
  }

  if (way.fragments.length > 1) return way.fragments.slice(1);
  return [way.fragments[0]];
}

/**
 * Decide whether a fragment is a bus segment that actually exposes a seat map.
 *
 * Flights, interurban (non-seat-selection) tenants, and open tickets should
 * all return false so the event reports `has_seat_selection: false`.
 *
 * @param {object} fragment - Fragment from purchase.departs/returns.
 * @returns {boolean} True when seat selection data is expected for this fragment.
 */
function fragmentAllowsSeatSelection(fragment) {
  if (!fragment) return false;
  if (fragment.transportType && fragment.transportType !== 'bus') return false;
  if (fragment.openTicket) return false;
  if (fragment.trip?.openTicket) return false;
  return Boolean(fragment.line?.allowsSeatSelection);
}

/**
 * Heuristic to detect a flat seats map (single-segment way shape).
 *
 * `purchase.<way>.seats` can either be a flat seats map
 * (`{ '1A': { number, occupied }, categories: [...] }`) when the way has no
 * connections, or a nested map keyed by `tripSlug` when it does. This check
 * looks for at least one entry that behaves like a seat (has a `number` or an
 * `occupied` flag) to decide on the flat shape.
 *
 * @param {object} map - Candidate seats object.
 * @returns {boolean} True when the map looks like a flat seats map.
 */
function looksLikeFlatSeatsMap(map) {
  if (!map || typeof map !== 'object') return false;
  return Object.keys(map).some((key) => {
    if (key === 'categories') return false;
    const value = map[key];
    return value && typeof value === 'object' && ('number' in value || 'occupied' in value);
  });
}

/**
 * Resolve the seats map for a fragment, trying every source the app uses.
 *
 * The seat map can live in four places depending on the flow that populated it:
 *   1. `seatsBus[tripSlug].seats` (Redux slice, set via `SET_SEAT_MAP_TRIPS`).
 *   2. `way.seats[tripSlug]` (purchase reducer, connection trips).
 *   3. `way.seats` as a flat map (purchase reducer, single-segment ways).
 *   4. `purchase.departs.seats` / `purchase.returns.seats` (checkout flow
 *      where seats were not promoted to `seatsBus`).
 *
 * Sources (2) and (3) require the caller to pass an explicit `way` object.
 * Source (4) is resolved automatically from the Redux store so that callers
 * that only have the tripSlug (e.g. `getTripSegmentsAvailability`) can still
 * capture seat data in the checkout flow.
 *
 * @param {string|undefined} tripSlug - Fragment id used as map key.
 * @param {object} [way] - Purchase departs/returns object that owns the fragment.
 * @returns {object|undefined} Seats map when found, otherwise undefined.
 */
function resolveSeatsMap(tripSlug, way) {
  const state = store.getState();
  const seatsBusState = state?.seatsBus;
  const seatsBus =
    typeof seatsBusState?.toJS === 'function' ? seatsBusState.toJS() : seatsBusState || {};
  const fromSeatsBus = tripSlug ? seatsBus?.[tripSlug]?.seats : undefined;
  if (fromSeatsBus && typeof fromSeatsBus === 'object') return fromSeatsBus;

  const waySeats = way?.seats;
  if (waySeats && typeof waySeats === 'object') {
    if (tripSlug && waySeats[tripSlug] && typeof waySeats[tripSlug] === 'object') {
      return waySeats[tripSlug];
    }
    if (looksLikeFlatSeatsMap(waySeats)) return waySeats;
  }

  if (tripSlug) {
    const purchaseState = state?.purchase;
    const purchase =
      typeof purchaseState?.toJS === 'function' ? purchaseState.toJS() : purchaseState || {};

    const resolvedFromPurchase = [purchase.departs, purchase.returns]
      .filter(Boolean)
      .reduce((found, purchaseWay) => {
        if (found) return found;
        const purchaseWaySeats = purchaseWay.seats;
        if (!purchaseWaySeats || typeof purchaseWaySeats !== 'object') return null;

        if (purchaseWaySeats[tripSlug] && typeof purchaseWaySeats[tripSlug] === 'object') {
          return purchaseWaySeats[tripSlug];
        }

        if (looksLikeFlatSeatsMap(purchaseWaySeats)) {
          const fragments = purchaseWay.fragments || [];
          const matchesFragment = fragments.some((f) => f?.id === tripSlug);
          if (matchesFragment) return purchaseWaySeats;
        }

        return null;
      }, null);

    if (resolvedFromPurchase) return resolvedFromPurchase;
  }

  return undefined;
}

/**
 * Pull total/occupied/selected seat data for a fragment, looking across all
 * the Redux slices where the seat map can live.
 *
 * @param {object} fragment - Fragment carrying the tripSlug (`fragment.id`).
 * @param {Array} [selectedSeatsForWay] - Flat selectedSeats array for the way.
 * @param {object} [way] - Purchase departs/returns the fragment belongs to.
 * @returns {{ totalSeats: (number|undefined), occupiedSeats: Array, selectedSeats: Array }}
 */
function getSegmentSeatData(fragment, selectedSeatsForWay = [], way) {
  const tripSlug = fragment?.id;
  const isConnection = Array.isArray(way?.fragments) && way.fragments.length > 1;

  // For connections, only count seats that are explicitly tagged to this
  // segment. Dropping untagged seats is safer than assigning them to every
  // leg, which would inflate selected_seats_count / available_seats_after.
  // For single-segment ways the fallback is safe because there is no
  // ambiguity about which segment a seat belongs to.
  const segmentSelectedSeats = isConnection
    ? selectedSeatsForWay.filter((seat) => seat?.tripSlug === tripSlug)
    : selectedSeatsForWay.filter((seat) => !seat?.tripSlug || seat.tripSlug === tripSlug);

  const seatsMap = resolveSeatsMap(tripSlug, way);
  if (!seatsMap || typeof seatsMap !== 'object') {
    return { totalSeats: undefined, occupiedSeats: [], selectedSeats: segmentSelectedSeats };
  }

  const allSeats = Object.keys(seatsMap)
    .filter((key) => key !== 'categories')
    .map((key) => seatsMap[key])
    .filter((seat) => seat && seat.number != null);

  const occupiedSeats = allSeats.filter((seat) => seat.occupied === true);

  return {
    totalSeats: allSeats.length,
    occupiedSeats,
    selectedSeats: segmentSelectedSeats,
  };
}

/**
 * Build the `segments_occupancy` array for a purchase, covering departs and
 * returns and preserving order: outbound segments first, then return segments.
 *
 * Each entry always carries a `segment_index` starting at 0. Entries without
 * seat selection data only expose `has_seat_selection: false`.
 *
 * @param {object} purchase - Purchase object as consumed by the trackers.
 * @returns {Array<object>|undefined} Array of per-segment occupancy payloads,
 *   or undefined when the purchase has no usable segment data.
 */
export function getSegmentsOccupancy(purchase) {
  if (!purchase) return undefined;

  const ways = [];
  if (purchase.departs) {
    ways.push({ way: purchase.departs, selectedSeats: purchase.departs.selectedSeats || [] });
  }
  if (purchase.returns) {
    ways.push({ way: purchase.returns, selectedSeats: purchase.returns.selectedSeats || [] });
  }

  const result = [];
  let index = 0;

  ways.forEach(({ way, selectedSeats }) => {
    const fragments = getWaySegmentFragments(way);

    fragments.forEach((fragment) => {
      const hasSeatSelection = fragmentAllowsSeatSelection(fragment);

      if (!hasSeatSelection) {
        result.push({ segment_index: index, has_seat_selection: false });
        index += 1;
        return;
      }

      const {
        totalSeats,
        occupiedSeats,
        selectedSeats: segmentSelected,
      } = getSegmentSeatData(fragment, selectedSeats, way);

      const occupancy = getSegmentAvailabilityAfterSelection({
        hasSeatSelection: true,
        totalSeats,
        occupiedSeats,
        selectedSeats: segmentSelected,
      });

      result.push({ segment_index: index, ...occupancy });
      index += 1;
    });
  });

  return result.length > 0 ? result : undefined;
}

/**
 * Build the `segments_availability` array for the trip shown in the seat map.
 *
 * `trackViewSeats` fires per single trip (the `SeatSelector` guards on
 * `!isMultiTrip`), so this helper always returns a one-element array. The
 * array shape is preserved for parity with `segments_occupancy`, which makes
 * downstream Mixpanel analyses interchangeable between both events.
 *
 * @param {object} trip - Trip object passed to `trackViewSeats`.
 * @returns {Array<object>|undefined} Array with a single segment availability
 *   entry, or undefined when no trip data is provided.
 */
export function getTripSegmentsAvailability(trip) {
  if (!trip) return undefined;

  // `trackViewSeats` receives a trip that is already the fragment being
  // displayed, so we evaluate seat-selection capability on the trip itself.
  const hasSeatSelection = fragmentAllowsSeatSelection({
    transportType: trip.transportType,
    openTicket: trip.openTicket,
    line: trip.line,
    trip: { openTicket: trip.openTicket },
  });

  if (!hasSeatSelection) {
    return [{ segment_index: 0, has_seat_selection: false }];
  }

  const { totalSeats, occupiedSeats } = getSegmentSeatData({ id: trip.id });

  return [
    {
      segment_index: 0,
      ...getSegmentAvailabilityBeforeSelection({
        hasSeatSelection: true,
        totalSeats,
        occupiedSeats,
      }),
    },
  ];
}
